/**
 * TripsStudio — panel de viajes para agencias (módulo «Ventas — Agencia de Viajes»).
 *
 * Rediseño pedido por Samuel (22-oct-2026): «que se vea la imagen, más intuitivo, que no
 * rompa con el resto del sistema». Antes: lista de texto sin fotos, datos básicos en un
 * modal oscuro (ilegible en tema claro) y el contenido en 10 pestañas escondidas.
 *
 * Ahora:
 *  - Cuadrícula de tarjetas con foto de portada, precio «desde» y estado (activos e inactivos).
 *  - Editor a pantalla completa: vista previa en vivo a la izquierda (lo que ve el cliente)
 *    y secciones a la derecha (Datos, Fotos, Precios, Qué incluye, Itinerario, Fechas…).
 *  - Solo tokens del tema (bg-card, text-foreground, border-border…): igual en claro y oscuro.
 *
 * Las secciones de contenido (fotos, precios, etc.) NO se reescriben: TravelAdmin las pasa
 * por `renderSection` y aquí solo se acomodan. Su lógica y sus endpoints quedan intactos.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft, BookOpen, Calendar, CheckCircle2, Clock, DollarSign, Eye, EyeOff, FileText,
  HelpCircle, Image as ImageIcon, ListOrdered, Loader, MapPin, Package, PawPrint, Plane,
  Plus, Save, Search, Target, Trash2, Users,
} from 'lucide-react';
import { createTrip, deleteTrip, updateTrip, type Trip } from '@/lib/api_travel';

export type TripSectionId =
  | 'datos' | 'fotos' | 'precios' | 'incluye' | 'itinerario'
  | 'fechas' | 'preguntas' | 'extras' | 'historia' | 'metas';

const SECCIONES: { id: TripSectionId; label: string; icon: React.ReactNode; ayuda: string }[] = [
  { id: 'datos', label: 'Datos', icon: <FileText size={15} />, ayuda: 'Nombre, horario, descripción y logística' },
  { id: 'fotos', label: 'Fotos', icon: <ImageIcon size={15} />, ayuda: 'Portada y galería que ve el cliente' },
  { id: 'precios', label: 'Precios', icon: <DollarSign size={15} />, ayuda: 'Planes y precio por persona' },
  { id: 'incluye', label: 'Qué incluye', icon: <CheckCircle2 size={15} />, ayuda: 'Lo que sí y lo que no incluye' },
  { id: 'itinerario', label: 'Itinerario', icon: <ListOrdered size={15} />, ayuda: 'Paso a paso de la experiencia' },
  { id: 'fechas', label: 'Fechas', icon: <Calendar size={15} />, ayuda: 'Salidas programadas' },
  { id: 'preguntas', label: 'Preguntas', icon: <HelpCircle size={15} />, ayuda: 'Preguntas frecuentes' },
  { id: 'extras', label: 'Adicionales', icon: <Package size={15} />, ayuda: 'Extras que se pueden sumar' },
  { id: 'historia', label: 'Historia', icon: <BookOpen size={15} />, ayuda: 'Concepto y relato del viaje' },
  { id: 'metas', label: 'Metas', icon: <Target size={15} />, ayuda: 'Para quién es y qué logra' },
];

const NIVELES = ['Básico', 'Fácil', 'Moderado', 'Difícil', 'Extremo'];

const VACIO: Partial<Trip> = {
  name: '', long_title: '', subtitle: '', slogan: '', location: '', short_description: '',
  duration: 3, duration_unit: 'hours', min_participants: 2, max_participants: 20, min_age: 6,
  max_age: 99, pets_allowed: true, physical_level: 2, wheelchair_accessible: false, active: true,
};

/** Campos que se guardan desde «Datos» (los mismos que el formulario anterior). */
const CAMPOS: (keyof Trip)[] = [
  'name', 'long_title', 'subtitle', 'slogan', 'location', 'short_description', 'duration',
  'duration_unit', 'min_participants', 'max_participants', 'min_age', 'max_age', 'pets_allowed',
  'physical_level', 'wheelchair_accessible', 'active', 'deposit_percentage', 'deposit_min_amount',
  'deposit_per_person',
];

function slugify(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const duracionTexto = (t: Partial<Trip>) => {
  const n = Number(t.duration || 0);
  if (!n) return '';
  const unidad = t.duration_unit === 'days' ? (n === 1 ? 'día' : 'días') : (n === 1 ? 'hora' : 'horas');
  return `${n} ${unidad}`;
};

/* ─────────────────────────── piezas de UI (solo tokens) ─────────────────────────── */

function Campo({ label, ayuda, children, className = '' }: {
  label: string; ayuda?: string; children: React.ReactNode; className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[13px] font-medium text-foreground">{label}</span>
      {ayuda && <span className="block text-xs text-muted-foreground mt-0.5">{ayuda}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const claseEntrada =
  'w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground ' +
  'placeholder:text-muted-foreground/70 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20';

function Entrada(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${claseEntrada} ${props.className || ''}`} />;
}

function Interruptor({ activo, onChange, etiqueta, icono }: {
  activo: boolean; onChange: (v: boolean) => void; etiqueta: string; icono?: React.ReactNode;
}) {
  return (
    <button type="button" onClick={() => onChange(!activo)} aria-pressed={activo}
      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition
        ${activo ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-border bg-background text-muted-foreground hover:text-foreground'}`}>
      <span className={`relative h-5 w-9 rounded-full transition ${activo ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition ${activo ? 'translate-x-4' : ''}`} />
      </span>
      {icono}{etiqueta}
    </button>
  );
}

function BotonPrimario({ children, onClick, disabled, type = 'button' }: {
  children: React.ReactNode; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit';
}) {
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50">
      {children}
    </button>
  );
}

function BotonSuave({ children, onClick, peligro, titulo }: {
  children: React.ReactNode; onClick: () => void; peligro?: boolean; titulo?: string;
}) {
  return (
    <button type="button" onClick={onClick} title={titulo} aria-label={titulo}
      className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm transition
        ${peligro ? 'border-destructive/30 text-destructive hover:bg-destructive/10'
                  : 'border-border bg-card text-foreground hover:bg-muted'}`}>
      {children}
    </button>
  );
}

function Portada({ url, nombre, className = '' }: { url?: string | null; nombre?: string; className?: string }) {
  const [rota, setRota] = useState(false);
  useEffect(() => setRota(false), [url]);
  if (url && !rota) {
    // object-top: muchos viajes usan el flyer como portada (vertical, con nombre y logo arriba).
    return <img src={url} alt={nombre || ''} loading="lazy" onError={() => setRota(true)}
      className={`h-full w-full object-cover object-top ${className}`} />;
  }
  return (
    <div className={`flex h-full w-full flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-primary/15 via-muted to-muted text-muted-foreground ${className}`}>
      <Plane size={26} className="opacity-60" />
      <span className="text-xs">Sin foto todavía</span>
    </div>
  );
}

function NivelFisico({ nivel }: { nivel: number }) {
  return (
    <span className="inline-flex items-end gap-0.5" title={`Nivel físico: ${NIVELES[nivel - 1] || nivel}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={`w-1 rounded-sm ${i <= nivel ? 'bg-primary' : 'bg-muted-foreground/25'}`}
          style={{ height: `${5 + i * 2.5}px` }} />
      ))}
    </span>
  );
}

/* ─────────────────────────── tarjeta del listado ─────────────────────────── */

function TarjetaViaje({ trip, onAbrir, onActivar, onEliminar }: {
  trip: Trip; onAbrir: () => void; onActivar: (v: boolean) => void; onEliminar: () => void;
}) {
  return (
    <article className={`group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg ${trip.active ? '' : 'opacity-75'}`}>
      <button type="button" onClick={onAbrir} className="relative block aspect-[16/10] overflow-hidden text-left" aria-label={`Editar ${trip.name}`}>
        <Portada url={trip.cover_image} nombre={trip.name}
          className={`transition duration-300 group-hover:scale-[1.03] ${trip.active ? '' : 'grayscale'}`} />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur
          ${trip.active ? 'bg-emerald-500/90 text-white' : 'bg-black/60 text-white'}`}>
          {trip.active ? 'Activo' : 'Inactivo'}
        </span>
        {trip.starting_price && (
          <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-slate-900 shadow">
            desde {trip.starting_price}
          </span>
        )}
      </button>
      <div className="flex flex-1 flex-col gap-2 p-3.5">
        <button type="button" onClick={onAbrir} className="text-left">
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-foreground hover:text-primary">{trip.name}</h3>
        </button>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {trip.location && <span className="inline-flex items-center gap-1"><MapPin size={12} />{trip.location}</span>}
          {duracionTexto(trip) && <span className="inline-flex items-center gap-1"><Clock size={12} />{duracionTexto(trip)}</span>}
          {trip.min_age > 0 && <span className="inline-flex items-center gap-1"><Users size={12} />desde {trip.min_age} años</span>}
        </div>
        {trip.slogan && <p className="line-clamp-2 text-xs text-foreground/80">{trip.slogan}</p>}
        <div className="mt-auto flex items-center justify-between pt-1">
          <span className="text-xs text-muted-foreground">
            {trip.plans_count ? `${trip.plans_count} ${trip.plans_count === 1 ? 'opción' : 'opciones'} de precio` : 'Sin precios'}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => onActivar(!trip.active)}
              title={trip.active ? 'Desactivar (el cliente deja de verlo)' : 'Activar'}
              aria-label={trip.active ? 'Desactivar' : 'Activar'}
              className="rounded-lg p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground">
              {trip.active ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
            <button type="button" onClick={onEliminar} title="Eliminar" aria-label="Eliminar"
              className="rounded-lg p-2 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive">
              <Trash2 size={15} />
            </button>
            <button type="button" onClick={onAbrir}
              className="ml-1 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/20">
              Editar
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ─────────────────────────── vista previa en vivo ─────────────────────────── */

function VistaPrevia({ datos, portada, precio }: { datos: Partial<Trip>; portada?: string | null; precio?: string | null }) {
  const pendientes = [
    !portada && 'Foto de portada',
    !precio && 'Precio',
    !(datos.short_description || '').trim() && 'Descripción',
    !(datos.slogan || '').trim() && 'Horario',
  ].filter(Boolean) as string[];
  return (
    <div className="space-y-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Así lo ve el cliente</p>
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="relative aspect-[4/3]">
          <Portada url={portada} nombre={datos.name} />
          {precio && (
            <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-slate-900 shadow">
              desde {precio}
            </span>
          )}
        </div>
        <div className="space-y-2 p-4">
          <h3 className="text-lg font-semibold leading-snug text-foreground">{datos.name || 'Nombre del viaje'}</h3>
          {datos.subtitle && <p className="text-sm text-muted-foreground">{datos.subtitle}</p>}
          {datos.slogan && (
            <p className="flex items-start gap-1.5 rounded-lg bg-muted px-2.5 py-2 text-xs text-foreground">
              <Clock size={13} className="mt-0.5 shrink-0 text-primary" />{datos.slogan}
            </p>
          )}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {datos.location && <Chip icono={<MapPin size={12} />}>{datos.location}</Chip>}
            {duracionTexto(datos) && <Chip icono={<Clock size={12} />}>{duracionTexto(datos)}</Chip>}
            <Chip icono={<Users size={12} />}>{datos.min_participants || 1}–{datos.max_participants || 20} personas</Chip>
            {Number(datos.min_age) > 0 && <Chip>Desde {datos.min_age} años</Chip>}
            {datos.pets_allowed && <Chip icono={<PawPrint size={12} />}>Pet friendly</Chip>}
            <Chip><NivelFisico nivel={Number(datos.physical_level) || 1} /><span className="ml-1">{NIVELES[(Number(datos.physical_level) || 1) - 1]}</span></Chip>
          </div>
          {datos.short_description && <p className="line-clamp-5 pt-1 text-sm text-foreground/85">{datos.short_description}</p>}
        </div>
      </div>
      {pendientes.length > 0 ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-foreground">
          <p className="font-medium">Le falta para verse completo:</p>
          <p className="mt-0.5 text-muted-foreground">{pendientes.join(' · ')}</p>
        </div>
      ) : (
        <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 size={14} />Listo para mostrarse
        </p>
      )}
    </div>
  );
}

function Chip({ children, icono }: { children: React.ReactNode; icono?: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
      {icono}{children}
    </span>
  );
}

/* ─────────────────────────── formulario «Datos» ─────────────────────────── */

function FormularioDatos({ datos, set }: { datos: Partial<Trip>; set: (k: keyof Trip, v: any) => void }) {
  const [anticipoAbierto, setAnticipoAbierto] = useState(
    !!(datos.deposit_percentage || datos.deposit_min_amount || datos.deposit_per_person),
  );
  const num = (v: string, min = 0) => Math.max(min, Number(v) || 0);
  return (
    <div className="space-y-5">
      <Bloque titulo="Lo que ve el cliente">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo label="Nombre" className="sm:col-span-2">
            <Entrada value={datos.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Ej. Cueva del Agua" autoFocus={!datos.id} />
          </Campo>
          <Campo label="Subtítulo" ayuda="Una línea que lo resume">
            <Entrada value={datos.subtitle || ''} onChange={(e) => set('subtitle', e.target.value)} placeholder="Ej. Senderismo y espeleísmo nocturno" />
          </Campo>
          <Campo label="Ubicación">
            <Entrada value={datos.location || ''} onChange={(e) => set('location', e.target.value)} placeholder="Ej. Xilitla, S.L.P." />
          </Campo>
          <Campo label="Horario y duración" className="sm:col-span-2"
            ayuda="El asistente de WhatsApp lo dice tal cual. Ej. «Salidas de 9:00 AM a 4:00 PM · 3 h aprox.»">
            <Entrada value={datos.slogan || ''} onChange={(e) => set('slogan', e.target.value)} placeholder="Salidas de … a … · … h aprox." />
          </Campo>
          <Campo label="Descripción" className="sm:col-span-2" ayuda="Qué se vive en la experiencia, en 2 o 3 frases">
            <textarea value={datos.short_description || ''} onChange={(e) => set('short_description', e.target.value)} rows={4}
              className={`${claseEntrada} resize-y`} placeholder="Cuéntale al cliente qué va a vivir…" />
            <span className="mt-1 block text-right text-[11px] text-muted-foreground">{(datos.short_description || '').length} caracteres</span>
          </Campo>
          <Campo label="Título largo" ayuda="Opcional, para el sitio" className="sm:col-span-2">
            <Entrada value={datos.long_title || ''} onChange={(e) => set('long_title', e.target.value)} />
          </Campo>
        </div>
      </Bloque>

      <Bloque titulo="Logística">
        <div className="grid gap-4 sm:grid-cols-4">
          <Campo label="Duración">
            <Entrada type="number" min={1} value={datos.duration ?? 1} onChange={(e) => set('duration', num(e.target.value, 1))} />
          </Campo>
          <Campo label="En">
            <select value={datos.duration_unit || 'hours'} onChange={(e) => set('duration_unit', e.target.value)} className={claseEntrada}>
              <option value="hours">Horas</option><option value="days">Días</option>
            </select>
          </Campo>
          <Campo label="Mínimo de personas">
            <Entrada type="number" min={1} value={datos.min_participants ?? 1} onChange={(e) => set('min_participants', num(e.target.value, 1))} />
          </Campo>
          <Campo label="Máximo de personas">
            <Entrada type="number" min={1} value={datos.max_participants ?? 20} onChange={(e) => set('max_participants', num(e.target.value, 1))} />
          </Campo>
          <Campo label="Edad mínima">
            <Entrada type="number" min={0} value={datos.min_age ?? 0} onChange={(e) => set('min_age', num(e.target.value))} />
          </Campo>
          <Campo label="Edad máxima">
            <Entrada type="number" min={0} value={datos.max_age ?? 99} onChange={(e) => set('max_age', num(e.target.value))} />
          </Campo>
          <Campo label="Nivel físico" className="sm:col-span-2">
            <div className="grid grid-cols-5 gap-1.5" role="radiogroup">
              {NIVELES.map((n, i) => {
                const sel = (Number(datos.physical_level) || 1) === i + 1;
                return (
                  <button key={n} type="button" role="radio" aria-checked={sel} onClick={() => set('physical_level', i + 1)}
                    className={`rounded-lg border px-1 py-2 text-[11px] transition ${sel ? 'border-primary bg-primary/10 font-semibold text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                    {n}
                  </button>
                );
              })}
            </div>
          </Campo>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Interruptor activo={!!datos.active} onChange={(v) => set('active', v)} etiqueta="Activo (lo ve el cliente)" />
          <Interruptor activo={!!datos.pets_allowed} onChange={(v) => set('pets_allowed', v)} etiqueta="Pet friendly" icono={<PawPrint size={14} />} />
          <Interruptor activo={!!datos.wheelchair_accessible} onChange={(v) => set('wheelchair_accessible', v)} etiqueta="Accesible" />
        </div>
      </Bloque>

      <Bloque titulo="Anticipo de este viaje" ayuda="Vacío = se usa la política de la empresa"
        abierto={anticipoAbierto} onToggle={() => setAnticipoAbierto((v) => !v)}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Campo label="% del total" ayuda="Ej. 15">
            <Entrada inputMode="decimal" value={(datos.deposit_percentage ?? '') as any} onChange={(e) => set('deposit_percentage', e.target.value)} placeholder="Empresa" />
          </Campo>
          <Campo label="Mínimo por reserva (MXN)" ayuda="Ej. 3000">
            <Entrada inputMode="decimal" value={(datos.deposit_min_amount ?? '') as any} onChange={(e) => set('deposit_min_amount', e.target.value)} placeholder="Empresa" />
          </Campo>
          <Campo label="Por persona (MXN)" ayuda="Si no hay %">
            <Entrada inputMode="decimal" value={(datos.deposit_per_person ?? '') as any} onChange={(e) => set('deposit_per_person', e.target.value)} placeholder="Empresa" />
          </Campo>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Con porcentaje, el anticipo es el mayor entre el mínimo y el % del total (nunca más que el total).
        </p>
      </Bloque>
    </div>
  );
}

function Bloque({ titulo, ayuda, children, abierto, onToggle }: {
  titulo: string; ayuda?: string; children: React.ReactNode; abierto?: boolean; onToggle?: () => void;
}) {
  const plegable = typeof abierto === 'boolean';
  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <header className={`flex items-baseline justify-between gap-3 ${plegable ? 'cursor-pointer' : ''}`} onClick={onToggle}>
        <div>
          <h4 className="text-sm font-semibold text-foreground">{titulo}</h4>
          {ayuda && <p className="text-xs text-muted-foreground">{ayuda}</p>}
        </div>
        {plegable && <span className="text-xs font-medium text-primary">{abierto ? 'Ocultar' : 'Configurar'}</span>}
      </header>
      {(!plegable || abierto) && <div className="mt-4">{children}</div>}
    </section>
  );
}

/* ─────────────────────────── editor a pantalla completa ─────────────────────────── */

function EditorViaje({ inicial, onCerrar, onGuardado, onEliminar, notify, renderSection }: {
  inicial: Partial<Trip>;
  onCerrar: () => void;
  onGuardado: (t: Trip) => void;
  onEliminar: (t: Trip) => void;
  notify: (t: any, m: string) => void;
  renderSection: (id: Exclude<TripSectionId, 'datos'>, trip: Trip) => React.ReactNode;
}) {
  const [datos, setDatos] = useState<Partial<Trip>>(inicial);
  const [base, setBase] = useState<Partial<Trip>>(inicial);
  const [seccion, setSeccion] = useState<TripSectionId>('datos');
  const [guardando, setGuardando] = useState(false);
  const nuevo = !datos.id;
  const sucio = useMemo(() => CAMPOS.some((k) => (datos as any)[k] !== (base as any)[k]), [datos, base]);
  const set = useCallback((k: keyof Trip, v: any) => setDatos((p) => ({ ...p, [k]: v })), []);

  const cerrar = useCallback(() => {
    if (sucio && !confirm('Tienes cambios sin guardar. ¿Salir de todos modos?')) return;
    onCerrar();
  }, [sucio, onCerrar]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') cerrar(); };
    window.addEventListener('keydown', h);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = overflow; };
  }, [cerrar]);

  const guardar = async () => {
    if (!(datos.name || '').trim()) { notify('error', 'Ponle nombre al viaje'); setSeccion('datos'); return; }
    setGuardando(true);
    try {
      const cuerpo: Record<string, any> = {};
      CAMPOS.forEach((k) => { if ((datos as any)[k] !== undefined) cuerpo[k] = (datos as any)[k]; });
      let guardado: Trip;
      if (nuevo) {
        guardado = await createTrip({ ...cuerpo, slug: slugify(datos.name || '') }) as Trip;
        notify('success', 'Viaje creado. Ahora agrégale fotos y precios.');
      } else {
        guardado = await updateTrip(datos.id!, cuerpo) as Trip;
        notify('success', 'Cambios guardados');
      }
      const siguiente = { ...datos, ...guardado };
      setDatos(siguiente); setBase(siguiente);
      onGuardado(guardado);
      if (nuevo) setSeccion('fotos');
    } catch (e: any) {
      notify('error', e?.message || 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  const actual = SECCIONES.find((s) => s.id === seccion)!;

  // Portal: .o-page usa backdrop-filter y eso vuelve "fixed" relativo a su caja (el editor
  // quedaba dentro del marco de la página y en móvil salía en blanco). Se monta en el
  // contenedor .orion-shell si existe (trae la paleta de la org en variables) y si no, en <body>.
  const destino = (document.querySelector('.orion-shell') as HTMLElement | null) || document.body;
  return createPortal(
    <div className="app-safe-full fixed inset-0 z-[60] flex flex-col bg-background text-foreground" role="dialog" aria-modal="true"
      aria-label={nuevo ? 'Nuevo viaje' : `Editar ${datos.name}`}>
      {/* barra superior */}
      <header className="flex shrink-0 items-center gap-3 border-b border-border bg-card/90 px-3 py-2.5 backdrop-blur sm:px-5">
        <button type="button" onClick={cerrar} aria-label="Volver a los viajes"
          className="rounded-xl p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground">
          <ArrowLeft size={18} />
        </button>
        <div className="h-9 w-12 shrink-0 overflow-hidden rounded-lg border border-border">
          <Portada url={datos.cover_image} nombre={datos.name} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{datos.name || 'Nuevo viaje'}</p>
          <p className="truncate text-xs text-muted-foreground">
            {nuevo ? 'Llena los datos básicos para empezar' : sucio ? 'Cambios sin guardar' : (datos.active ? 'Activo · lo ve el cliente' : 'Inactivo · el cliente no lo ve')}
          </p>
        </div>
        {!nuevo && (
          <BotonSuave peligro titulo="Eliminar viaje" onClick={() => onEliminar(datos as Trip)}>
            <Trash2 size={15} /><span className="hidden md:inline">Eliminar</span>
          </BotonSuave>
        )}
        {seccion === 'datos' && (
          <BotonPrimario onClick={guardar} disabled={guardando || (!sucio && !nuevo)}>
            {guardando ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
            {nuevo ? 'Crear viaje' : 'Guardar'}
          </BotonPrimario>
        )}
      </header>

      <div className="o-page o-page--flush min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-7xl gap-6 px-3 py-5 sm:px-5 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
          {/* vista previa */}
          <aside className="lg:sticky lg:top-5 lg:self-start">
            <VistaPrevia datos={datos} portada={datos.cover_image} precio={datos.starting_price} />
          </aside>

          {/* secciones */}
          <main className="min-w-0 space-y-4">
            <nav className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 lg:flex-wrap lg:overflow-visible" aria-label="Secciones del viaje">
              {SECCIONES.map((s) => {
                const bloqueada = nuevo && s.id !== 'datos';
                const on = s.id === seccion;
                return (
                  <button key={s.id} type="button" disabled={bloqueada} onClick={() => setSeccion(s.id)}
                    title={bloqueada ? 'Primero crea el viaje' : s.ayuda}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition
                      ${on ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                           : 'border-border bg-card text-muted-foreground hover:text-foreground'}
                      disabled:cursor-not-allowed disabled:opacity-40`}>
                    {s.icon}{s.label}
                  </button>
                );
              })}
            </nav>
            <div>
              <h3 className="text-base font-semibold">{actual.label}</h3>
              <p className="text-sm text-muted-foreground">{actual.ayuda}</p>
            </div>
            {seccion === 'datos'
              ? <FormularioDatos datos={datos} set={set} />
              : <section className="o-card">{renderSection(seccion, datos as Trip)}</section>}
          </main>
        </div>
      </div>
    </div>,
    destino,
  );
}

/* ─────────────────────────── panel principal ─────────────────────────── */

type Filtro = 'todos' | 'activos' | 'inactivos';

export function TripsStudio({ trips, notify, refresh, renderSection }: {
  trips: Trip[];
  notify: (t: any, m: string) => void;
  refresh: () => Promise<void>;
  renderSection: (id: Exclude<TripSectionId, 'datos'>, trip: Trip) => React.ReactNode;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [editando, setEditando] = useState<Partial<Trip> | null>(null);
  // Llave fija mientras el editor está abierto: al crear un viaje (id nuevo) no se
  // remonta y no pierde la sección en la que estaba.
  const [llave, setLlave] = useState(0);
  const abrir = (t: Partial<Trip>) => { setLlave((n) => n + 1); setEditando(t); };

  const visibles = useMemo(() => {
    const q = busqueda.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    return trips
      .filter((t) => filtro === 'todos' || (filtro === 'activos' ? t.active : !t.active))
      .filter((t) => !q || `${t.name} ${t.location} ${t.subtitle}`.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().includes(q))
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name, 'es'));
  }, [trips, busqueda, filtro]);

  const cuenta = { todos: trips.length, activos: trips.filter((t) => t.active).length, inactivos: trips.filter((t) => !t.active).length };

  const activar = async (t: Trip, v: boolean) => {
    try { await updateTrip(t.id, { active: v }); notify('success', v ? `«${t.name}» activado` : `«${t.name}» desactivado: el cliente ya no lo ve`); await refresh(); }
    catch (e: any) { notify('error', e?.message || 'No se pudo cambiar'); }
  };
  const eliminar = async (t: Trip) => {
    if (!confirm(`¿Eliminar «${t.name}»? Esto no se puede deshacer. Si solo quieres ocultarlo, mejor desactívalo.`)) return;
    try { await deleteTrip(t.id); notify('success', 'Viaje eliminado'); setEditando(null); await refresh(); }
    catch (e: any) { notify('error', e?.message || 'No se pudo eliminar'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-xs">
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar viaje o lugar…"
              className={`${claseEntrada} pl-9`} aria-label="Buscar viaje" />
          </div>
          <div className="inline-flex rounded-xl border border-border bg-card p-1" role="tablist">
            {(['todos', 'activos', 'inactivos'] as Filtro[]).map((f) => (
              <button key={f} type="button" role="tab" aria-selected={filtro === f} onClick={() => setFiltro(f)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition
                  ${filtro === f ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                {f} <span className="opacity-70">{cuenta[f]}</span>
              </button>
            ))}
          </div>
        </div>
        <BotonPrimario onClick={() => abrir({ ...VACIO })}><Plus size={16} />Nuevo viaje</BotonPrimario>
      </div>

      {visibles.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {visibles.map((t) => (
            <TarjetaViaje key={t.id} trip={t} onAbrir={() => abrir({ ...VACIO, ...t })}
              onActivar={(v) => activar(t, v)} onEliminar={() => eliminar(t)} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-16 text-center">
          <Plane size={30} className="text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {trips.length === 0 ? 'Todavía no hay viajes. Crea el primero y súbele fotos.' : 'Ningún viaje coincide con la búsqueda.'}
          </p>
          {trips.length === 0 && <BotonPrimario onClick={() => abrir({ ...VACIO })}><Plus size={16} />Crear el primer viaje</BotonPrimario>}
        </div>
      )}

      {editando && (
        <EditorViaje
          key={llave}
          inicial={editando}
          notify={notify}
          renderSection={renderSection}
          onCerrar={() => { setEditando(null); refresh(); }}
          onGuardado={(t) => { setEditando((p) => (p ? { ...p, ...t } : p)); refresh(); }}
          onEliminar={eliminar}
        />
      )}
    </div>
  );
}
